import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { Badge } from '@/components/common/Badge';
import { theme } from '@/styles/theme';
import forestImage from '@/assets/themes/forest_image.png';
import beachImage from '@/assets/themes/beach_image.png';
import libraryImage from '@/assets/themes/library_image.jpeg';

const Title = styled.h2`
  text-align: center;
  margin-bottom: ${theme.spacing.xxxl};
  color: ${theme.colors.text.black};
`;

const ThemesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ThemeCard = styled(motion.div)`
  position: relative;
  border-radius: ${theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${theme.shadows.medium};
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:hover {
    transform: scale(1.02);
    box-shadow: ${theme.shadows.large};
  }
`;

const ThemeImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
`;

const ThemeOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.7));
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.white};
`;

const ThemeName = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.white};
  margin-bottom: ${theme.spacing.sm};
`;

const ThemeDescription = styled.p`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.white};
  margin: 0;
  opacity: 0.9;
`;

const BadgeWrapper = styled.div`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
`;

const themes = [
  {
    name: 'Forest',
    description: 'Natural, calming focus',
    image: forestImage,
    isFree: true,
  },
  {
    name: 'Beach',
    description: 'Peaceful ocean vibes',
    image: beachImage,
    isFree: false,
  },
  {
    name: 'Library',
    description: 'Classic, sophisticated setting',
    image: libraryImage,
    isFree: false,
  },
];

export const ThemeShowcaseSection = () => {
  return (
    <Section $padding="large">
      <Container>
        <Title>Choose your perfect learning atmosphere</Title>
        <ThemesGrid>
          {themes.map((themeItem, index) => (
            <ThemeCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <ThemeImage src={themeItem.image} alt={`${themeItem.name} theme`} />
              <ThemeOverlay>
                <ThemeName>{themeItem.name}</ThemeName>
                <ThemeDescription>{themeItem.description}</ThemeDescription>
              </ThemeOverlay>
              <BadgeWrapper>
                <Badge $variant={themeItem.isFree ? 'success' : 'warning'}>
                  {themeItem.isFree ? 'Free' : 'Premium'}
                </Badge>
              </BadgeWrapper>
            </ThemeCard>
          ))}
        </ThemesGrid>
      </Container>
    </Section>
  );
};
